import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

interface Media {
  id: string;
  url: string;
  thumbnailUrl?: string;
  filename: string;
  type?: 'image' | 'video';
  createdAt?: string;
}

interface SlideshowProps {
  images: Array<Media>;
}

const SlideshowContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  background-color: black;
  animation: fadeIn 0.5s ease-out;
`;

const SlideImage = styled.img`
  width: 100%;
  height: 65vh;
  object-fit: contain;
  transition: transform 0.5s ease, opacity 0.5s ease;
  display: block;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    height: 45vh;
  }
`;

const VideoContainer = styled.div`
  width: 100%;
  height: 65vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  @media (max-width: 768px) {
    height: 45vh;
  }
`;

const VideoLoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  z-index: 5;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 5px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 12px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const SlideVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  max-height: 100%;
  transition: opacity 0.3s ease;
`;

const VideoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #000;
  color: white;
  cursor: pointer;
`;

const NavigationButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: 15px;' : 'right: 15px;'}
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  opacity: 0.7;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
    opacity: 1;
    transform: translateY(-50%) scale(1.1);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  z-index: 10;
`;

const DownloadButton = styled.a`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: linear-gradient(to right, var(--primary-color), var(--primary-dark));
  color: white;
  text-decoration: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s;
  z-index: 10;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.4);
  }
  
  svg {
    margin-right: 8px;
  }
`;

const MediaTypeIndicator = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  z-index: 10;
  
  svg {
    margin-right: 6px;
  }
`;

const ThumbnailContainer = styled.div`
  display: flex;
  overflow-x: auto;
  padding: 15px 0;
  background-color: rgba(0, 0, 0, 0.8);
  scrollbar-width: thin;
  scrollbar-color: var(--primary-color) rgba(0, 0, 0, 0.5);
  position: relative;
  scroll-behavior: smooth;
  margin-top: 15px;
  border-radius: var(--border-radius);

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--primary-color);
    border-radius: 3px;
  }
`;

const ThumbnailItem = styled.div<{ active: boolean }>`
  position: relative;
  height: 70px;
  width: 100px;
  margin: 0 5px;
  cursor: pointer;
  opacity: ${props => props.active ? 1 : 0.6};
  border: 3px solid ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  border-radius: 6px;
  transition: all 0.3s ease;
  box-shadow: ${props => props.active ? '0 0 10px rgba(67, 97, 238, 0.7)' : 'none'};
  overflow: hidden;

  &:hover {
    opacity: 1;
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    height: 50px;
    width: 70px;
  }
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VideoThumbnailOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 24px;
    height: 24px;
    color: white;
  }
`;

// Icon components
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 9H15V3H9V9H5L12 16L19 9ZM5 18V20H19V18H5Z" fill="currentColor"/>
  </svg>
);

const LeftArrowIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor"/>
  </svg>
);

const RightArrowIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 6L8.59 7.41L13.17 12L8.59 16.59L10 18L16 12L10 6Z" fill="currentColor"/>
  </svg>
);

const VideoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 10.5V7C17 6.45 16.55 6 16 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H16C16.55 18 17 17.55 17 17V13.5L21 17.5V6.5L17 10.5Z" fill="currentColor"/>
  </svg>
);

const ImageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.5L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor"/>
  </svg>
);

const PlayIcon = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
  </svg>
);

/**
 * Get the appropriate MIME type based on file extension
 * @param filename Filename or path with extension
 * @returns MIME type string for video
 */
const getVideoMimeType = (filename: string): string => {
  const ext = filename.toLowerCase().split('.').pop();
  
  const mimeTypes: Record<string, string> = {
    'mp4': 'video/mp4',
    'webm': 'video/webm',
    'mov': 'video/quicktime',
    'avi': 'video/x-msvideo',
    'wmv': 'video/x-ms-wmv'
  };
  
  return mimeTypes[ext || ''] || 'video/mp4';
};

const API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:2001';

const SlideshowOptimized: React.FC<SlideshowProps> = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const currentMedia = images[currentIndex];
  const isVideo = currentMedia?.type === 'video';
  const hasThumbnail = !!currentMedia?.thumbnailUrl;

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Reset video state when changing slides
  useEffect(() => {
    setVideoLoaded(false);
    setVideoPlaying(false);
    setVideoLoading(false);
  }, [currentIndex]);

  const goToPrevious = () => {
    // Pause video if playing
    if (isVideo && videoRef.current) {
      videoRef.current.pause();
    }
    
    setCurrentIndex(prevIndex => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    // Pause video if playing
    if (isVideo && videoRef.current) {
      videoRef.current.pause();
    }
    
    setCurrentIndex(prevIndex => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToSlide = (index: number) => {
    // Pause video if playing
    if (isVideo && videoRef.current) {
      videoRef.current.pause();
    }
    
    setCurrentIndex(index);
  };

  // Handle touch events for swipe
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Handle video events
  const handleVideoPlay = () => {
    setVideoPlaying(true);
  };

  const handleVideoPause = () => {
    setVideoPlaying(false);
  };

  const handleVideoLoadStart = () => {
    setVideoLoading(true);
  };

  const handleVideoLoaded = () => {
    setVideoLoaded(true);
    setVideoLoading(false);
  };

  const handleVideoError = (e: any) => {
    setVideoLoading(false);
    console.error('Error loading video:', currentMedia.url, e);
    
    // Try to stream from the API endpoint if direct access fails
    if (videoRef.current && currentMedia.id) {
      const streamUrl = `${API_BASE_URL}/api/media/stream/${currentMedia.id}`;
      console.log('Trying alternative stream URL:', streamUrl);
      videoRef.current.src = streamUrl;
      videoRef.current.load();
    }
  };

  const playVideo = () => {
    if (videoRef.current) {
      setVideoLoading(true);
      
      // Use stream API endpoint if direct URL is failing
      if (currentMedia.id && videoRef.current.src !== `${API_BASE_URL}/api/media/stream/${currentMedia.id}`) {
        videoRef.current.src = `${API_BASE_URL}/api/media/stream/${currentMedia.id}`;
      }
      
      videoRef.current.load();
      videoRef.current.play().catch(err => {
        console.error('Error playing video:', err);
        setVideoLoading(false);
      });
    }
  };

  // Add keyboard event listeners for navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === ' ' && isVideo && videoRef.current) {
        // Toggle play/pause on spacebar
        if (videoRef.current.paused) {
          videoRef.current.play();
        } else {
          videoRef.current.pause();
        }
        e.preventDefault(); // Prevent page scroll
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVideo]);

  // Scroll the current thumbnail into view
  useEffect(() => {
    const thumbnail = document.getElementById(`thumbnail-${currentIndex}`);
    if (thumbnail && thumbnailContainerRef.current) {
      thumbnail.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [currentIndex]);

  if (images.length === 0) {
    return <div>No media to display</div>;
  }

  return (
    <>
      <SlideshowContainer 
        ref={containerRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {isVideo ? (
          <VideoContainer>
            {/* Video optimized for CDN delivery */}
            <SlideVideo 
              ref={videoRef}
              controls
              controlsList="nodownload"
              preload="metadata"
              poster={currentMedia.thumbnailUrl || ''}
              onPlay={handleVideoPlay}
              onPause={handleVideoPause}
              onLoadStart={handleVideoLoadStart}
              onLoadedData={handleVideoLoaded}
              onError={handleVideoError}
              playsInline
              style={{ display: videoLoaded ? 'block' : 'none' }}
            >
              <source 
                src={currentMedia.id ? 
                  `${window.location.protocol}//${window.location.host}/api/media/stream/${currentMedia.id}` : 
                  currentMedia.url
                } 
                type={getVideoMimeType(currentMedia.filename)} 
              />
              Your browser does not support the video tag.
            </SlideVideo>
            
            {/* Loading indicator */}
            {videoLoading && (
              <VideoLoadingOverlay>
                <LoadingSpinner />
                <div>Loading video...</div>
              </VideoLoadingOverlay>
            )}
            
            {/* Placeholder with play button when video not loaded */}
            {!videoLoaded && !videoLoading && (
              <VideoPlaceholder onClick={playVideo}>
                {hasThumbnail ? (
                  <div style={{ 
                    position: 'relative', 
                    width: '100%', 
                    height: '100%',
                    backgroundImage: `url(${currentMedia.thumbnailUrl})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.3)'
                    }}>
                      <PlayIcon />
                    </div>
                  </div>
                ) : (
                  <>
                    <PlayIcon />
                    <div style={{ marginTop: '15px' }}>Click to play video</div>
                  </>
                )}
              </VideoPlaceholder>
            )}
          </VideoContainer>
        ) : (
          // Regular image display
          <SlideImage 
            src={currentMedia.url} 
            alt={currentMedia.filename}
            loading="lazy"
          />
        )}
        
        <NavigationButton direction="left" onClick={goToPrevious} aria-label="Previous media">
          <LeftArrowIcon />
        </NavigationButton>
        
        <NavigationButton direction="right" onClick={goToNext} aria-label="Next media">
          <RightArrowIcon />
        </NavigationButton>
        
        <ImageCounter>
          {currentIndex + 1} / {images.length}
        </ImageCounter>
        
        <MediaTypeIndicator>
          {isVideo ? <VideoIcon /> : <ImageIcon />}
          {isVideo ? 'Video' : 'Image'}
        </MediaTypeIndicator>
        
        <DownloadButton 
          href="#"
          onClick={(e) => {
            // Apply the same download approach for both images and videos
            e.preventDefault();
            
            // Use the download attribute of an anchor element to force download
            const link = document.createElement('a');
            
            // Use the ID-based download endpoint for both images and videos
            if (currentMedia.id) {
              link.href = `${API_BASE_URL}/api/media/download/${currentMedia.id}`;
            } else {
              // Fallback to direct URL if ID is not available (should be rare)
              link.href = currentMedia.url;
            }
            
            link.download = currentMedia.filename; // This triggers download instead of navigation
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          aria-label={`Download ${isVideo ? 'video' : 'image'}`}
        >
          <DownloadIcon />
          Download
        </DownloadButton>
      </SlideshowContainer>
      
      {/* <ThumbnailContainer ref={thumbnailContainerRef}>
        {images.map((media, index) => (
          <ThumbnailItem 
            key={media.id}
            id={`thumbnail-${index}`}
            active={index === currentIndex}
            onClick={() => goToSlide(index)}
          >
            <Thumbnail 
              src={media.thumbnailUrl || media.url} 
              alt={`Thumbnail ${index}`}
              loading="lazy"
            />
            {media.type === 'video' && (
              <VideoThumbnailOverlay>
                <PlayIcon />
              </VideoThumbnailOverlay>
            )}
          </ThumbnailItem>
        ))}
      </ThumbnailContainer> */}
    </>
  );
};

export default SlideshowOptimized;